/**
 * The words for a scenario (PRD 7.5; Phase 35), shared by the server and the client so the two
 * cannot disagree about what an override is called or when a scenario is out of date.
 *
 * Pure: no DOM, no fetch. Everything here takes the API's own shapes — a scenario as
 * `GET /scenarios` returns it, an override as it is stored — and returns words and small
 * structures. The arithmetic stays on the server; this module only ever says what a number is.
 */

/** What each override kind is called, and the sentence that says what it does. */
export const OVERRIDE_META = Object.freeze(
  [
    {
      kind: 'stage_exclude',
      label: 'Stage out',
      noun: 'stage',
      sentence: 'Deals in this stage write no pipeline demand in this scenario.',
    },
    {
      kind: 'opportunity_exclude',
      label: 'Deal out',
      noun: 'deal',
      sentence: 'This deal writes no pipeline demand in this scenario.',
    },
    {
      kind: 'delivery_shift',
      label: 'Delivery moved',
      noun: 'project',
      sentence:
        'The project’s dates move before the forecast reads them, so its window, its curve and its burn are all recomputed on the moved plan.',
    },
    {
      kind: 'role_mix',
      label: 'Role mix',
      noun: 'project',
      sentence:
        'The project’s hours per week are unchanged; each week is re-split across these services, and the rows stop naming a person.',
    },
    {
      kind: 'capacity',
      label: 'Capacity',
      noun: 'person',
      sentence: 'This person’s weekly hours are replaced for the window, as a manual source would.',
    },
    {
      kind: 'elastic_source',
      label: 'Elastic capacity',
      noun: 'source',
      sentence: 'This elastic source joins the capacity line.',
    },
  ].map(Object.freeze),
);

const META_BY_KIND = new Map(OVERRIDE_META.map((m) => [m.kind, m]));

/** The words for one kind; an unknown kind keeps its key rather than vanishing. */
export function overrideMeta(kind) {
  return META_BY_KIND.get(kind) ?? { kind, label: String(kind ?? ''), noun: '', sentence: '' };
}

const weeksWord = (n) => `${Math.abs(n)} week${Math.abs(n) === 1 ? '' : 's'}`;

/** The role mix as `design 60% · development 40%`, largest share first. */
export function mixLabel(mix) {
  return Object.entries(mix ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([service, share]) => `${service} ${Math.round(share * 100)}%`)
    .join(' · ');
}

/**
 * One override as a line a planner can read: what it is, what it names, and what it does to it.
 * @param {object} o  a `scenario_overrides` row in API shape
 */
export function describeOverride(o) {
  const meta = overrideMeta(o?.kind);
  switch (o?.kind) {
    case 'stage_exclude':
      return { ...meta, subject: o.stage_name ?? o.stage_key, effect: 'out of the pipeline' };
    case 'opportunity_exclude':
      return {
        ...meta,
        subject: o.opportunity_name ?? 'A deal',
        effect: 'out of the pipeline',
      };
    case 'delivery_shift':
      return {
        ...meta,
        subject: [o.project_number, o.project_name].filter(Boolean).join(' ') || 'A project',
        effect: `${weeksWord(o.shift_weeks)} ${o.shift_weeks < 0 ? 'earlier' : 'later'}`,
      };
    case 'role_mix':
      return {
        ...meta,
        subject: [o.project_number, o.project_name].filter(Boolean).join(' ') || 'A project',
        effect: mixLabel(o.role_mix),
      };
    case 'capacity': {
      const window = o.active_to
        ? `${o.active_from ?? 'the start'} to ${o.active_to}`
        : o.active_from
          ? `from ${o.active_from}`
          : 'the whole horizon';
      return {
        ...meta,
        subject: o.user_name ?? 'Somebody',
        effect: `${o.weekly_hours} h a week, ${window}`,
      };
    }
    case 'elastic_source':
      return {
        ...meta,
        subject: o.source_name ?? 'An elastic source',
        effect: o.weekly_hours ? `${o.weekly_hours} h a week on the line` : 'on the line',
      };
    default:
      return { ...meta, subject: '', effect: '' };
  }
}

/**
 * Where a scenario stands, in the four states a reader acts on differently.
 *
 * `uncomputed` and `stale` are not the same thing and never read the same: the first has no
 * numbers at all, the second has numbers that answer an older question. A scenario whose last
 * run failed says so rather than showing the run before it.
 *
 * @param {object} scenario  as `GET /scenarios` returns it
 */
export function scenarioState(scenario) {
  const run = scenario?.last_run ?? null;
  if (!run) return { key: 'uncomputed', label: 'Not computed', tone: 'idle' };
  if (run.status === 'running') return { key: 'running', label: 'Computing…', tone: 'busy' };
  if (run.status === 'failed') return { key: 'failed', label: 'Last run failed', tone: 'bad' };
  if (scenario.stale) return { key: 'stale', label: 'Inputs changed since', tone: 'warn' };
  return { key: 'ready', label: 'Computed', tone: 'good' };
}

/** Whether a scenario has numbers a view can be read through. */
export const isReadable = (scenario) =>
  scenario?.last_run?.status === 'succeeded' && !!scenario.last_run.id;

/**
 * The scenario in one line of chips: what it changes, counted by kind, plus the two toggles that
 * live on the scenario itself. A scenario with nothing in it says so, because an empty scenario
 * computes to the base forecast and a reader should not have to work that out from silence.
 *
 * @param {object} scenario
 * @param {object[]} overrides  its overrides, when they have been loaded
 */
export function summarize(scenario, overrides = scenario?.overrides ?? []) {
  const chips = [];
  const counts = new Map();
  for (const o of overrides) counts.set(o.kind, (counts.get(o.kind) ?? 0) + 1);
  for (const meta of OVERRIDE_META) {
    const n = counts.get(meta.kind);
    if (!n) continue;
    chips.push({ kind: meta.kind, label: `${meta.label}${n > 1 ? ` ×${n}` : ''}` });
  }
  if (scenario?.pipeline_mode)
    chips.push({ kind: 'pipeline_mode', label: `Pipeline ${scenario.pipeline_mode}` });
  if (scenario?.include_categories?.length)
    chips.push({
      kind: 'include_categories',
      label: `Stages: ${scenario.include_categories.join(', ')}`,
    });
  if (scenario?.include_elastic && !counts.get('elastic_source'))
    chips.push({ kind: 'elastic', label: 'Elastic capacity on' });
  if (!chips.length) chips.push({ kind: 'empty', label: 'Same as the base forecast' });
  return chips;
}

/**
 * The one sentence a scenario's diff is worth: how its demand and capacity differ from the base,
 * and which week moved most. Takes the diff response; returns null when there is nothing to say.
 */
export function diffHeadline(diff) {
  const totals = diff?.totals;
  if (!totals) return null;
  const hours = Number(totals.hours_delta ?? 0);
  const capacity = Number(totals.capacity_delta ?? 0);
  if (!hours && !capacity) return { hours, capacity, text: 'No difference from the base run.' };
  const parts = [];
  if (hours) parts.push(`${hours > 0 ? '+' : ''}${hours} h of demand`);
  if (capacity) parts.push(`${capacity > 0 ? '+' : ''}${capacity} h of capacity`);
  return { hours, capacity, text: `${parts.join(', ')} against the base run.` };
}
